<template>
  <div :class="category" class="state-effect">
    <div class="basic-options">
      <el-select
        v-close-on-zoom
        :value="state.effect.id"
        :placeholder="placeholder"
        class="state-select"
        :disabled="readonly || state.states.length <= 1"
        @input="stateKey => selectState(stateKey)"
      >
        <el-option
          v-for="stateEnum in state.states"
          :key="stateEnum.key"
          :label="stateEnum.name.en"
          :value="stateEnum.key"
        />
      </el-select>

      <state-input
        v-if="state.effect.id"
        :state="statesByKey[state.effect.id]"
        :initial-value="state.effect.value"
        :show-label="false"
        :width="width"
        :disabled="readonly"
        :is-absolute="state.effect.isAbsolute"
        :use-default="useDefault"
        @input="value => updateStateValue(value)"
      >
        <template slot="prefix">
          {{
            (category !== 'vital' && category !== 'labResult') ||
              state.effect.isAbsolute
              ? 'to'
              : 'by'
          }}
        </template>
      </state-input>

      <div v-if="category === 'eyeState'" :style="{ 'margin-left': '8px' }">
        <md-checkbox
          :value="state.effect.nonreactive"
          :disabled="readonly"
          @change="updateNonreactive"
        >
          Nonreactive
        </md-checkbox>
      </div>

      <el-input-number
        v-if="state.effect.id && !noOverTime"
        :value="state.effect.duration"
        class="duration-input"
        :controls="false"
        placeholder="Duration"
        :disabled="readonly"
        @input="duration => updateEffectDuration(duration)"
      >
        <template slot="prepend">over</template>
        <template slot="append">s</template>
      </el-input-number>

      <absolute-warning-tooltip v-if="state.effect.isAbsolute" />
    </div>
    <advanced-options-card
      v-if="hasAdvancedOptions"
      :initial-open="!!state.effect.isAbsolute"
    >
      <el-checkbox
        v-model="state.effect.isAbsolute"
        label="Absolute Effect"
        :disabled="readonly"
        @change="update"
      />
      <absolute-warning-tooltip />
    </advanced-options-card>
  </div>
</template>

<script>
import keyBy from 'lodash/keyBy';

import StateInput from '~lib/common/components/StateInput';
// CMS does not recognize that HelpTooltip is being used
// eslint-disable-next-line no-unused-vars
import HelpTooltip from '~lib/common/components/HelpTooltip';
import AdvancedOptionsCard from './common/AdvancedOptionsCard';

export default {
  name: 'state-effect',
  components: {
    StateInput,
    AdvancedOptionsCard,
    AbsoluteWarningTooltip: {
      render() {
        return (
          <HelpTooltip type="warning" direction="top">
            Use with care. Absolute state effects cancel currently running state
            effects of that type,
            <br />
            but its follow up Checks, Effects, Timers, and Effect Chains will
            continue as normal. <br />
            Any subsequent relative state effects will be delayed until the end
            of this effect.
          </HelpTooltip>
        );
      },
    },
  },
  props: [
    'states',
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData',
    'control',
    'category',
    'type',
    'placeholder',
    'useDefault',
    'hasAdvancedOptions',
    'noOverTime',
  ],
  data() {
    const categoryStates = this.states.filter(
      s => s.category === this.category,
    );
    return {
      state: {
        states: categoryStates,
        effect: {
          id: categoryStates[0].key,
          nonreactive: undefined
        },
      },
      value: 0,
      width: this.category === 'waveform' ? '200px' : '100px',
    };
  },
  computed: {
    statesByKey() {
      return keyBy(this.state.states, 'key');
    },
  },
  created() {
    this.state.effect = {
      ...this.state.effect,
      ...(this.getData(this.ikey) || {}),
    };
    this.update();
  },
  methods: {
    selectState(stateKey) {
      this.state.effect.id = stateKey;
      this.update();
    },
    updateStateValue(value) {
      this.state.effect.value = value;
      this.update();
    },
    updateEffectDuration(duration) {
      this.state.effect.duration = duration;
      this.update();
    },
    updateNonreactive(value) {
      this.state.effect.nonreactive = value;
      this.update();
    },
    update() {
      this.putData(this.ikey, { type: this.type, ...this.state.effect });
      this.emitter.trigger('process', this.control);
      this.$forceUpdate();
    },
  },
};
</script>

<style scoped>
.state-effect.waveform,
.state-effect.eyeState {
  width: 450px;
}

.state-effect .state-select {
  width: 130px;
  margin-right: 5px;
}
.state-effect.waveform .state-select,
.state-effect.waveform .state-input,
.state-effect.eyeState .state-select,
.state-effect.eyeState .state-input {
  width: 160px !important;
}

.basic-options {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.duration-input {
  width: 125px;
  margin-left: auto;
}

.help-tooltip {
  margin-left: 3px;
}
</style>

<style>
.state-select input {
  text-transform: capitalize;
}
</style>
